import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import api from '../../services/api';
import Swal from 'sweetalert2';
import AdminLayout from '../../components/AdminLayout';
import WysiwygEditor from '../../components/WysiwygEditor';
import DateInput from '../../components/DateInput';
import { isValidPixelId } from '../../utils/metaPixel';
import { isValidTikTokPixelId } from '../../utils/tiktokPixel';
import { 
  ThemesAndLayout,
  SectionManager,
  ProductSelection, 
  FormSelection,
  InstantWinConfig,
  CashbackConfig,
  EmailSettings,
  WinnerDraw,
  CampaignSettings,
  QRCodeGenerator,
  MembershipConfig
} from '../../components/campaign';

import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import {
  DEFAULT_TIME_ZONE,
  utcToZonedTimeString,
  zonedTimeToUtc
} from '../../utils/timezone';
import {
  ArrowLeft,
  Trash2,
  CloudUpload,
  FileText,
  Package,
  Palette,
  ClipboardList,
  PartyPopper,
  Banknote,
  Ticket,
  Mail,
  QrCode,
  Trophy,
  Settings,
} from 'lucide-react';

// For static assets, use ASSET_URL in dev, hostname in prod
const ASSET_BASE_URL = process.env.REACT_APP_ASSET_URL || process.env.REACT_APP_HOSTNAME || '';
const DEFAULT_RECEIPT_SYSTEM_PROMPT = `Extract receipt data. Use exact wording from the receipt and keep unusual quantity formats (e.g., "4X6X330ml") exactly as written. For each product, include the full line with brand, size, quantity, and units and make sure the product price reflects the total shown on the receipt line. Return ONLY JSON: {"purchaseDate":"YYYY-MM-DD","storeName":"name","storeAddress":"address","products":[{"name":"FULL product name copied from receipt","price":0.00}]}. No explanation.`;
const DEFAULT_RECEIPT_USER_PROMPT = 'Receipt:\n{{OCR_TEXT}}';

function CampaignEdit() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabFromQuery = params.get('tab');
    if (tabFromQuery && tabFromQuery !== activeTab) {
      setActiveTab(tabFromQuery);
    }
  }, [location.search, activeTab]);
  const [emailSettings, setEmailSettings] = useState({
    submission: {
      is_enabled: false,
      recipient_emails: '',
      from_name: '',
      from_email_address: '',
      email_subject: '',
      email_body: ''
    },
    instant_win: {
      is_enabled: false,
      recipient_emails: '',
      from_name: '',
      from_email_address: '',
      email_subject: '',
      email_body: ''
    },
    rejected: {
      is_enabled: false,
      recipient_emails: '',
      from_name: '',
      from_email_address: '',
      email_subject: '',
      email_body: ''
    }
  });
  const [savingEmailSettings, setSavingEmailSettings] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [newFaq, setNewFaq] = useState({ question: '', answer: '' });
  const [gallery, setGallery] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [availableBrands, setAvailableBrands] = useState([]);
  const [showSectionTypeModal, setShowSectionTypeModal] = useState(false);
  const [selectedSectionType, setSelectedSectionType] = useState('');
  const [theme, setTheme] = useState({
    primary_color: '#1F2937',
    secondary_color: '#FFFFFF',
    accent_color: '#4F46E5',
    logo_url: '',
    custom_css: '',
    thank_you_message: 'Thank you for participating!',
    winner_message: '',
    loser_message: '',
    font_settings: {
      header_font_name: '',
      header_font_file_url: '',
      paragraph_font_name: '',
      paragraph_font_file_url: ''
    }
  });
  const [sections, setSections] = useState([]);
  const [winnerSections, setWinnerSections] = useState([]);
  const [comingSoonSections, setComingSoonSections] = useState([]);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [forms, setForms] = useState([]); // Campaign-specific forms
  const [allForms, setAllForms] = useState([]); // All available forms
  const [activeForm, setActiveForm] = useState(null);
  const [formFields, setFormFields] = useState([]); // Fields for the active form
  const [instantWinPrizes, setInstantWinPrizes] = useState([]);
  const [instantWinEnabled, setInstantWinEnabled] = useState(false);
  const [instantWinDistribution, setInstantWinDistribution] = useState('low_to_high');
  const [instantWinStats, setInstantWinStats] = useState(null);
  const [cashbackVoucherEnabled, setCashbackVoucherEnabled] = useState(false);
  const [instantWinConfig, setInstantWinConfig] = useState({});
  const [instantWinConfigText, setInstantWinConfigText] = useState('');
  const [prizeAllocations, setPrizeAllocations] = useState({});
  const [openProductDropdowns, setOpenProductDropdowns] = useState({});
  const [availableMechanics, setAvailableMechanics] = useState([]);
  
  // Winner Draw states
  
  // Notification states
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    terms_and_conditions: '',
    custom_domain: '',
    slug: '',
    meta_pixel_id: '',
    tiktok_pixel_id: '',
    step1_system_prompt: DEFAULT_RECEIPT_SYSTEM_PROMPT,
    step1_user_prompt: DEFAULT_RECEIPT_USER_PROMPT,
    step2_system_prompt: '',
    step2_user_prompt: '',
    step3_system_prompt: '',
    step3_user_prompt: '',
    start_date: '',
    end_date: '',
    mechanics_type_id: '',
    mechanics_type: 'simple_entry',
    mechanics_type_code: '',
    max_entries_per_user: 1,
    unlimited_entries: false,
    max_total_entries: '',
    unlimited_total_entries: false,
    requires_receipt: true,
    skip_receipt_validation: false,
    entry_limit_period: 'none',
    entry_limit_timezone: 'America/New_York'
  });


  useEffect(() => {
    fetchCampaign();
    fetchAllProducts();
    fetchInstantWinPrizes();
    fetchAvailableMechanics();
  }, [id]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside any dropdown container
      const dropdownContainer = event.target.closest('[data-dropdown-container]');
      const dropdownTrigger = event.target.closest('[data-dropdown-trigger]');
      
      // If clicking outside both container and trigger, close all dropdowns
      if (!dropdownContainer && !dropdownTrigger) {
        setOpenProductDropdowns({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch form fields when active form changes
  useEffect(() => {
    if (activeForm?.id) {
      fetchFormFields(activeForm.id);
    } else {
      setFormFields([]);
    }
  }, [activeForm]);

  useEffect(() => {
    if (activeTab === 'instantwin' && instantWinEnabled) {
      fetchInstantWinStats();
      fetchInstantWinConfig();
    }
  }, [activeTab, instantWinEnabled]);



  const fetchFormFields = async (formId) => {
    try {
      const response = await api.get(`/admin/forms/${formId}/fields`);
      setFormFields(response.data.fields || []);
    } catch (err) {
      console.error('Failed to fetch form fields:', err);
      setFormFields([]);
    }
  };

  const fetchInstantWinPrizes = async () => {
    try {
      const response = await api.get(`/admin/campaigns/${id}/instant-win/prizes`);
      const prizes = response.data.prizes || [];
      setInstantWinPrizes(prizes);
      
      // Initialize allocations from stored data
      const newAllocations = {};
      prizes.forEach(prize => {
        if (prize.allocations) {
          const allocations = typeof prize.allocations === 'string' 
            ? JSON.parse(prize.allocations) 
            : prize.allocations;
          newAllocations[`prize_${prize.id}_allocations`] = allocations.allocations || [];
        }
      });
      setPrizeAllocations(newAllocations);
    } catch (err) {
      console.error('Failed to fetch instant win prizes:', err);
    }
  };

  const fetchInstantWinStats = async () => {
    try {
      const response = await api.get(`/admin/campaigns/${id}/instant-win/stats`);
      setInstantWinStats(response.data);
    } catch (err) {
      console.error('Failed to fetch instant win stats:', err);
    }
  };

  const fetchInstantWinConfig = async () => {
    try {
      const response = await api.get(`/admin/campaigns/${id}/instant-win/config`);
      const config = response.data.config || {};
      setInstantWinConfig(config);
      setInstantWinConfigText(JSON.stringify(config, null, 2));
    } catch (err) {
      console.error('Failed to fetch instant win config:', err);
      setInstantWinConfigText('{}');
    }
  };

  const fetchAvailableMechanics = async () => {
    try {
      const response = await api.get('/admin/mechanics/all');
      setAvailableMechanics(response.data.mechanics || []);
    } catch (err) {
      console.error('Failed to fetch available mechanics:', err);
    }
  };

  const updateInstantWinConfig = async () => {
    try {
      const parsed = JSON.parse(instantWinConfigText);
      await api.patch(`/admin/campaigns/${id}/instant-win`, { config: parsed });
      setInstantWinConfig(parsed);
      setSuccessMessage('Configuration saved successfully!');
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (err) {
      if (err instanceof SyntaxError) {
        alert('Invalid JSON format. Please check your configuration.');
      } else {
        console.error('Failed to update instant win config:', err);
        alert('Failed to update configuration');
      }
    }
  };


  const normalizeDbDateToLocal = (input) => {
    if (!input || typeof input !== 'string') return '';
    const trimmed = input.trim();
    
    // MySQL DATETIME "YYYY-MM-DD HH:mm[:ss]" -> "YYYY-MM-DDTHH:mm"
    if (trimmed.includes(' ')) {
      const parts = trimmed.split(' ');
      if (parts.length === 2) {
        const datePart = parts[0];
        const timePart = parts[1].split(':').slice(0, 2).join(':');
        return `${datePart}T${timePart}`;
      }
    }
    
    // ISO format with or without timezone: extract date/time components directly from string
    // This preserves the exact time without any timezone conversion
    const isoMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::\d{2})?(?:\.\d+)?(?:([+-]\d{2}:\d{2}|Z))?$/i);
    if (isoMatch) {
      const [, datePart, hour, minute] = isoMatch;
      return `${datePart}T${hour}:${minute}`;
    }
    
    return '';
  };

  const fetchCampaign = async () => {
    try {
      // When opened after create-with-template, apply passed theme/sections so Theme & Layout shows data immediately
      const state = location.state || {};
      if (state.initialTheme) {
        setTheme(state.initialTheme);
      }
      if (Array.isArray(state.initialSections)) {
        setSections(state.initialSections);
      }
      if (Array.isArray(state.initialWinnerSections)) {
        setWinnerSections(state.initialWinnerSections);
      }
      if (Array.isArray(state.initialComingSoonSections)) {
        setComingSoonSections(state.initialComingSoonSections);
      }

      const response = await api.get(`/admin/campaigns/${id}`);
      const campaignData = response.data.campaign;
      setCampaign(campaignData);
      
      const startDateFormatted = normalizeDbDateToLocal(campaignData.start_date);
      const endDateFormatted = normalizeDbDateToLocal(campaignData.end_date);
      
      setFormData({
        name: campaignData.name,
        description: campaignData.description || '',
        terms_and_conditions: campaignData.terms_and_conditions || '',
        custom_domain: campaignData.custom_domain || '',
        slug: campaignData.slug || '',
        meta_pixel_id: campaignData.meta_pixel_id || '',
        tiktok_pixel_id: campaignData.tiktok_pixel_id || '',
        step1_system_prompt: campaignData.step1_system_prompt || DEFAULT_RECEIPT_SYSTEM_PROMPT,
        step1_user_prompt: campaignData.step1_user_prompt || DEFAULT_RECEIPT_USER_PROMPT,
        step2_system_prompt: campaignData.step2_system_prompt || '',
        step2_user_prompt: campaignData.step2_user_prompt || '',
        step3_system_prompt: campaignData.step3_system_prompt || '',
        step3_user_prompt: campaignData.step3_user_prompt || '',
        start_date: startDateFormatted,
        end_date: endDateFormatted,
        mechanics_type_id: campaignData.mechanics_type_id,
        mechanics_type: campaignData.mechanics_type, // Keep for display
        mechanics_type_code: campaignData.mechanics_type_code || '',
        max_entries_per_user: campaignData.max_entries_per_user === 0 ? 1 : campaignData.max_entries_per_user,
        unlimited_entries: campaignData.max_entries_per_user === 0,
        max_total_entries: campaignData.max_total_entries || '',
        unlimited_total_entries: !campaignData.max_total_entries,
        requires_receipt: campaignData.requires_receipt,
        skip_receipt_validation: !!campaignData.skip_receipt_validation,
        entry_limit_period: campaignData.entry_limit_period || 'none',
        entry_limit_timezone: campaignData.entry_limit_timezone || 'America/New_York'
      });
      
      // Set instant win status
      setInstantWinEnabled(campaignData.instant_win_enabled || false);
      setInstantWinDistribution(campaignData.instant_win_distribution || 'low_to_high');
      // Set cashback voucher status
      setCashbackVoucherEnabled(campaignData.cashback_voucher_enabled || false);
      
      if (response.data.faqs) {
        setFaqs(response.data.faqs);
      }
      
      if (response.data.gallery) {
        setGallery(response.data.gallery);
      }
      
      if (response.data.products) {
        setSelectedProducts(response.data.products.map(p => p.id));
      }

      // Fetch campaign sections for landing page
      try {
        const landingResponse = await api.get(`/admin/campaigns/${id}/campaign-sections?page_type=landing_page`);
        setSections(landingResponse.data.sections || []);
      } catch (sectionsError) {
        console.warn('Could not fetch landing page sections:', sectionsError);
        setSections([]);
      }
      
      // Fetch campaign sections for winner page
      try {
        const winnerResponse = await api.get(`/admin/campaigns/${id}/campaign-sections?page_type=winner_page`);
        setWinnerSections(winnerResponse.data.sections || []);
      } catch (sectionsError) {
        console.warn('Could not fetch winner page sections:', sectionsError);
        setWinnerSections([]);
      }
      
      // Fetch campaign sections for coming soon page
      try {
        const comingSoonResponse = await api.get(`/admin/campaigns/${id}/campaign-sections?page_type=coming_soon_page`);
        setComingSoonSections(comingSoonResponse.data.sections || []);
      } catch (sectionsError) {
        console.warn('Could not fetch coming soon page sections:', sectionsError);
        setComingSoonSections([]);
      }
      
      // Fetch all forms and campaign-specific forms
      try {
        // Get all forms
        const allFormsResponse = await api.get('/admin/forms');
        setAllForms(allFormsResponse.data.forms || []);
        
        // Get campaign-specific forms
        const formsResponse = await api.get(`/admin/forms?campaign_id=${id}`);
        setForms(formsResponse.data.forms || []);
        // Find the active form
        const active = formsResponse.data.forms?.find(f => f.is_active);
        setActiveForm(active || null);
      } catch (err) {
        console.error('Failed to fetch forms:', err);
      }
      
      // Fetch instant win prizes
      try {
        const prizesResponse = await api.get(`/admin/campaigns/${id}/instant-win/prizes`);
        setInstantWinPrizes(prizesResponse.data.prizes || []);
      } catch (err) {
        console.error('Failed to fetch instant win prizes:', err);
      }
      
      if (response.data.theme) {
        setTheme({
          primary_color: response.data.theme.primary_color || '#1F2937',
          secondary_color: response.data.theme.secondary_color || '#FFFFFF',
          accent_color: response.data.theme.accent_color || '#4F46E5',
          logo_url: response.data.theme.logo_url || '',
          background_image_url: response.data.theme.background_image_url || '',
          custom_css: response.data.theme.custom_css || '',
          about_header: response.data.theme.about_header || 'About This Campaign',
          about_description: response.data.theme.about_description || '',
          faq_header: response.data.theme.faq_header || 'Frequently Asked Questions',
          faq_description: response.data.theme.faq_description || '',
          font_settings: response.data.theme.font_settings || {
            header_font_name: '',
            header_font_file_url: '',
            paragraph_font_name: '',
            paragraph_font_file_url: ''
          },
          thank_you_message: response.data.theme.thank_you_message || 'Thank you for participating!',
          winner_message: response.data.theme.winner_message || '',
          loser_message: response.data.theme.loser_message || '',
          full_width_background: response.data.theme.full_width_background !== false,
          section_order: response.data.theme.section_order || null,
          font_settings: response.data.theme.font_settings || {
            header_font_name: '',
            header_font_file_url: '',
            paragraph_font_name: '',
            paragraph_font_file_url: ''
          }
        });
        // Section order is now managed via the sections system
      }
      
      // Fetch email settings
      try {
        const emailResponse = await api.get(`/admin/campaigns/${id}/email-settings`);
        setEmailSettings(emailResponse.data.emailSettings || emailSettings);
      } catch (err) {
        console.error('Failed to fetch email settings:', err);
      }

      setLoading(false);
    } catch (err) {
      console.error('Fetch campaign error:', err);
      setError('Failed to load campaign');
      setLoading(false);
    }
  };

  const fetchAllProducts = async () => {
    try {
      const response = await api.get('/admin/products');
      const products = response.data.products || [];
      setAllProducts(products);
      
      // Extract unique brands from all products
      const brands = [...new Set(products.map(p => p.brand).filter(brand => brand))];
      setAvailableBrands(brands.sort());
    } catch (err) {
      console.error('Fetch products error:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      if (formData.custom_domain && formData.custom_domain.trim() !== '') {
        const trimmedDomain = formData.custom_domain.trim();
        if (trimmedDomain.startsWith('http://') || trimmedDomain.startsWith('https://')) {
          setError('Please enter the custom domain without http:// or https://');
          setSaving(false);
          return;
        }
        const domainRegex = /^[a-zA-Z0-9.-]+(:\d+)?$/;
        if (!domainRegex.test(trimmedDomain)) {
          setError('Custom domain can only contain letters, numbers, dots, hyphens, and an optional port');
          setSaving(false);
          return;
        }
      }

      if (formData.meta_pixel_id && !isValidPixelId(formData.meta_pixel_id)) {
        setError('Meta Pixel ID must be numeric (5-20 digits)');
        setSaving(false);
        return;
      }

      if (formData.tiktok_pixel_id && !isValidTikTokPixelId(formData.tiktok_pixel_id)) {
        setError('TikTok Pixel ID must be 10-32 letters or numbers');
        setSaving(false);
        return;
      }

      const trimmedSlug = (formData.slug || '').trim().toLowerCase();
      if (!trimmedSlug ||!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(trimmedSlug)) {
        setError('URL slug is required and can only contain lowercase letters, numbers, and single hyphens');
        setSaving(false);
        return;
      }

      // Validate date strings (expecting YYYY-MM-DDTHH:mm)
      if (!formData.start_date || !formData.end_date) {
        setError('Invalid start or end date');
        setSaving(false);
        return;
      }

      const formattedData = {
        ...formData,
        // Send local Sydney time as provided (backend will format to MySQL DATETIME)
        start_date: formData.start_date,
        end_date: formData.end_date,
        max_entries_per_user: formData.unlimited_entries ? 0 : parseInt(formData.max_entries_per_user),
        max_total_entries: (formData.unlimited_total_entries || !formData.max_total_entries) ? null : parseInt(formData.max_total_entries)
      };

      formattedData.slug = trimmedSlug;
      formattedData.custom_domain = formData.custom_domain && formData.custom_domain.trim() !== ''
        ? formData.custom_domain.trim().toLowerCase()
        : null;
      formattedData.meta_pixel_id = (formData.meta_pixel_id || '').trim();
      formattedData.tiktok_pixel_id = (formData.tiktok_pixel_id || '').trim();
      formattedData.step1_system_prompt =
        formData.step1_system_prompt && formData.step1_system_prompt.trim() !== ''
          ? formData.step1_system_prompt
          : null;
      formattedData.step1_user_prompt =
        formData.step1_user_prompt && formData.step1_user_prompt.trim() !== ''
          ? formData.step1_user_prompt
          : null;
      
      // Validation prompts
      formattedData.step2_system_prompt =
        formData.step2_system_prompt && formData.step2_system_prompt.trim() !== ''
          ? formData.step2_system_prompt
          : null;
      formattedData.step2_user_prompt =
        formData.step2_user_prompt && formData.step2_user_prompt.trim() !== ''
          ? formData.step2_user_prompt
          : null;
      formattedData.step3_system_prompt =
        formData.step3_system_prompt && formData.step3_system_prompt.trim() !== ''
          ? formData.step3_system_prompt
          : null;
      formattedData.step3_user_prompt =
        formData.step3_user_prompt && formData.step3_user_prompt.trim() !== ''
          ? formData.step3_user_prompt
          : null;
      
      // Remove the frontend-only flags before sending
      delete formattedData.unlimited_entries;
      delete formattedData.unlimited_total_entries;

      await api.put(`/admin/campaigns/${id}`, formattedData);

      setCampaign(prev => prev ? {
        ...prev,
        name: formattedData.name,
        slug: formattedData.slug,
        custom_domain: formattedData.custom_domain
      } : prev);
      setFormData(prev => ({ ...prev, slug: formattedData.slug }));

      setSuccessMessage('Campaign saved successfully!');
      setShowSuccess(true);
      setSaving(false);
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Update campaign error:', err);
      setError(err.response?.data?.error || 'Failed to update campaign');
      setSaving(false);
    }
  };

  const handleAddFaq = async () => {
    if (!newFaq.question.trim() || !newFaq.answer.trim()) {
      alert('Please enter both question and answer');
      return;
    }

    try {
      const response = await api.post(`/admin/campaigns/${id}/faqs`, {
        question: newFaq.question,
        answer: newFaq.answer,
        sort_order: faqs.length
      });
      
      setFaqs([...faqs, response.data.faq]);
      setNewFaq({ question: '', answer: '' });
    } catch (err) {
      console.error('Add FAQ error:', err);
      alert('Failed to add FAQ');
    }
  };

  const handleUpdateFaq = async (faqId, updatedFaq) => {
    try {
      await api.put(`/admin/campaigns/${id}/faqs/${faqId}`, updatedFaq);
      setFaqs(faqs.map(faq => faq.id === faqId ? { ...faq, ...updatedFaq } : faq));
    } catch (err) {
      console.error('Update FAQ error:', err);
      alert('Failed to update FAQ');
    }
  };

  const handleDeleteFaq = async (faqId) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    
    try {
      await api.delete(`/admin/campaigns/${id}/faqs/${faqId}`);
      setFaqs(faqs.filter(faq => faq.id !== faqId));
    } catch (err) {
      console.error('Delete FAQ error:', err);
      alert('Failed to delete FAQ');
    }
  };


  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      alert('Only JPG and PNG images are allowed');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }
    
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('image', file);
    formData.append('title', '');
    formData.append('description', '');
    
    try {
      const response = await api.post(
        `/admin/campaigns/${id}/gallery`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setGallery([...gallery, response.data.image]);
      e.target.value = '';
    } catch (err) {
      console.error('Upload image error:', err);
      alert('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteGalleryImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    try {
      await api.delete(`/admin/campaigns/${id}/gallery/${imageId}`);
      setGallery(gallery.filter(img => img.id !== imageId));
    } catch (err) {
      console.error('Delete image error:', err);
      alert('Failed to delete image');
    }
  };

  const handleThemeUpdate = (field, value) => {
    // Handle font_settings nested object
    if (field === 'header_font_name' || field === 'header_font_file_url' || 
        field === 'paragraph_font_name' || field === 'paragraph_font_file_url') {
      const updatedTheme = {
        ...theme,
        font_settings: {
          ...theme.font_settings,
          [field]: value
        }
      };
      setTheme(updatedTheme);
    } else {
      const updatedTheme = { ...theme, [field]: value };
      setTheme(updatedTheme);
    }
  };

  const handleSaveTheme = async () => {
    try {
      // Prepare theme data with font settings
      const themeData = {
        ...theme,
        header_font_name: theme.font_settings?.header_font_name || '',
        header_font_file_url: theme.font_settings?.header_font_file_url || '',
        paragraph_font_name: theme.font_settings?.paragraph_font_name || '',
        paragraph_font_file_url: theme.font_settings?.paragraph_font_file_url || ''
      };
      // Remove font_settings from the payload as we're sending individual fields
      delete themeData.font_settings;
      
      await api.put(`/admin/campaigns/${id}/theme`, themeData);
      // Show success notification
      setSuccessMessage('Theme saved successfully!');
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Update theme error:', err);
      setErrorMessage('Failed to update theme. Please try again.');
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 5000);
    }
  };

  const handleApplyTemplate = (data) => {
    if (data.theme) {
      setTheme({
        primary_color: data.theme.primary_color || '#1F2937',
        secondary_color: data.theme.secondary_color || '#FFFFFF',
        accent_color: data.theme.accent_color || '#4F46E5',
        logo_url: data.theme.logo_url || '',
        background_image_url: data.theme.background_image_url || '',
        custom_css: data.theme.custom_css || '',
        about_header: data.theme.about_header || 'About This Campaign',
        about_description: data.theme.about_description || '',
        faq_header: data.theme.faq_header || 'Frequently Asked Questions',
        faq_description: data.theme.faq_description || '',
        font_settings: data.font_settings ? {
          header_font_name: data.font_settings.header_font_name || '',
          header_font_file_url: data.font_settings.header_font_file_url || '',
          paragraph_font_name: data.font_settings.paragraph_font_name || '',
          paragraph_font_file_url: data.font_settings.paragraph_font_file_url || ''
        } : (theme.font_settings || {}),
        thank_you_message: data.theme.thank_you_message || 'Thank you for participating!',
        winner_message: data.theme.winner_message || '',
        loser_message: data.theme.loser_message || '',
        full_width_background: data.theme.full_width_background !== false,
        section_order: data.theme.section_order || null,
      });
    }
    if (data.sections) setSections(data.sections);
    if (data.winnerSections) setWinnerSections(data.winnerSections);
    if (data.comingSoonSections) setComingSoonSections(data.comingSoonSections);
  };

  // Section Management Functions
  const [currentPageType, setCurrentPageType] = useState('landing_page');

  const getSectionsByType = (pageType) => {
    switch (pageType) {
      case 'landing_page':
        return sections;
      case 'winner_page':
        return winnerSections;
      case 'coming_soon_page':
        return comingSoonSections;
      default:
        return sections;
    }
  };

  const updateSectionsByType = (pageType, updater) => {
    switch (pageType) {
      case 'landing_page':
        setSections(prev => updater(prev));
        break;
      case 'winner_page':
        setWinnerSections(prev => updater(prev));
        break;
      case 'coming_soon_page':
        setComingSoonSections(prev => updater(prev));
        break;
      default:
        setSections(prev => updater(prev));
        break;
    }
  };
  
  const handleAddSection = async (pageType = 'landing_page') => {
    setCurrentPageType(pageType);
    setShowSectionTypeModal(true);
    setSelectedSectionType('content'); // Default selection
  };
  
  const handleAddLandingSection = () => handleAddSection('landing_page');
  const handleAddWinnerSection = () => handleAddSection('winner_page');
  const handleAddComingSoonSection = () => handleAddSection('coming_soon_page');

  const handleConfirmAddSection = async () => {
    if (!selectedSectionType) return;

    setShowSectionTypeModal(false);

    try {
      const targetSections = getSectionsByType(currentPageType);
      const response = await api.post(`/admin/campaigns/${id}/campaign-sections`, {
        type: selectedSectionType,
        page_type: currentPageType,
        sort_order: targetSections.length
      });

      updateSectionsByType(currentPageType, (prev) => [...prev, response.data.section]);
    } catch (error) {
      console.error('Failed to add section:', error);
      alert('Failed to add section');
    }
  };

  const handleDeleteSection = async (sectionId, pageType = 'landing_page') => {
    try {
      await api.delete(`/admin/campaigns/${id}/campaign-sections/${sectionId}`);
      updateSectionsByType(pageType, (prev) => prev.filter(section => section.id !== sectionId));
    } catch (error) {
      console.error('Failed to delete section:', error);
      alert('Failed to delete section');
    }
  };
  
  const handleDeleteLandingSection = (sectionId) => handleDeleteSection(sectionId, 'landing_page');
  const handleDeleteWinnerSection = (sectionId) => handleDeleteSection(sectionId, 'winner_page');
  const handleDeleteComingSoonSection = (sectionId) => handleDeleteSection(sectionId, 'coming_soon_page');

  const handleDuplicateSection = async (section, pageType = 'landing_page') => {
    try {
      const targetSections = getSectionsByType(pageType);
      const duplicateData = {
        type: section.type,
        page_type: pageType,
        background_image_url: section.background_image_url,
        full_width_background: section.full_width_background,
        about_header: section.about_header ? `${section.about_header} (Copy)` : null,
        about_description: section.about_description,
        about_with_form: section.about_with_form,
        faqs_header: section.faqs_header ? `${section.faqs_header} (Copy)` : null,
        faqs_description: section.faqs_description,
        sort_order: targetSections.length
      };

      const response = await api.post(`/admin/campaigns/${id}/campaign-sections`, duplicateData);
      updateSectionsByType(pageType, (prev) => [...prev, response.data.section]);
    } catch (error) {
      console.error('Failed to duplicate section:', error);
      alert('Failed to duplicate section');
    }
  };
  
  const handleDuplicateLandingSection = (section) => handleDuplicateSection(section, 'landing_page');
  const handleDuplicateWinnerSection = (section) => handleDuplicateSection(section, 'winner_page');
  const handleDuplicateComingSoonSection = (section) => handleDuplicateSection(section, 'coming_soon_page');

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'logo');
    
    try {
      const response = await api.post(
        `/admin/campaigns/${id}/theme/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      handleThemeUpdate('logo_url', response.data.url);
      e.target.value = '';
      setSuccessMessage('Logo uploaded successfully');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      console.error('Upload logo error:', err);
      setErrorMessage(err.response?.data?.error || err.message || 'Failed to upload logo');
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await api.delete(`/admin/campaigns/${id}/theme/logo`);
      handleThemeUpdate('logo_url', '');
      setSuccessMessage('Logo removed');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      console.error('Remove logo error:', err);
      setErrorMessage(err.response?.data?.error || err.message || 'Failed to remove logo');
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    }
  };

  const handleBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingBackground(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'background');

    try {
      const response = await api.post(
        `/admin/campaigns/${id}/theme/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      handleThemeUpdate('background_image_url', response.data.url);
      e.target.value = '';
      setSuccessMessage('Background uploaded successfully');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      console.error('Upload background error:', err);
      setErrorMessage(err.response?.data?.error || err.message || 'Failed to upload background');
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    } finally {
      setUploadingBackground(false);
    }
  };

  const handleRemoveBackground = () => {
    handleThemeUpdate('background_image_url', '');
    setSuccessMessage('Background removed');
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  // Drag and drop handlers
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Sortable Item Component

  const handleTermsUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      alert('Please select a PDF file');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('terms_file', file);

      const response = await api.post(`/admin/campaigns/${id}/upload-terms`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update form data with the new file path
      setFormData(prev => ({
        ...prev,
        terms_and_conditions: response.data.filePath
      }));

      // Show success message
      setSuccessMessage('Terms and conditions uploaded successfully');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);

    } catch (err) {
      console.error('Upload terms error:', err);
      if (err.response?.data?.error) {
        alert(err.response.data.error);
      } else {
        alert('Failed to upload terms and conditions');
      }
    }

    // Reset file input
    e.target.value = '';
    
    // Also reset the replace file input if it exists
    const replaceInput = document.getElementById('terms-file-replace');
    if (replaceInput) {
      replaceInput.value = '';
    }
  };

  const handleDeleteTerms = async () => {
    if (!window.confirm('Are you sure you want to delete the terms and conditions PDF?')) {
      return;
    }

    try {
      await api.delete(`/admin/campaigns/${id}/terms`);

      // Update form data to remove the file path
      setFormData(prev => ({
        ...prev,
        terms_and_conditions: ''
      }));

      // Show success message
      setSuccessMessage('Terms and conditions deleted successfully');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);

    } catch (err) {
      console.error('Delete terms error:', err);
      if (err.response?.data?.error) {
        alert(err.response.data.error);
      } else {
        alert('Failed to delete terms and conditions');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  const mechanicsCode = (formData.mechanics_type_code || formData.mechanics_type || '').toLowerCase();
  const campaignTabs = [
    { id: 'details', label: 'Campaign Details', shortLabel: 'Details', icon: FileText },
    { id: 'products', label: `Products (${selectedProducts.length})`, shortLabel: `Products (${selectedProducts.length})`, icon: Package },
    { id: 'theme', label: 'Theme & Layout', shortLabel: 'Theme', icon: Palette },
    { id: 'forms', label: 'Forms', shortLabel: 'Forms', icon: ClipboardList },
    { id: 'instantwin', label: 'Instant Win', shortLabel: 'Instant Win', icon: PartyPopper },
    ...(mechanicsCode === 'cashback' ? [{ id: 'cashback', label: 'Cashback', shortLabel: 'Cashback', icon: Banknote }] : []),
    ...(mechanicsCode === 'membership' ? [{ id: 'membership', label: 'Membership', shortLabel: 'Membership', icon: Ticket }] : []),
    { id: 'emails', label: 'Email Notifications', shortLabel: 'Emails', icon: Mail },
    { id: 'qrcode', label: 'QR Code', shortLabel: 'QR', icon: QrCode },
    { id: 'winnerdraw', label: 'Winner Draw', shortLabel: 'Draw', icon: Trophy },
    { id: 'settings', label: 'Settings', shortLabel: 'Settings', icon: Settings }
  ];

  const selectCampaignTab = (tabId) => {
    setActiveTab(tabId);
    navigate(`/admin/campaigns/${id}/edit?tab=${tabId}`, { replace: true });
  };

  return (
    <AdminLayout activeSection="CAMPAIGN">
      <style>
        {`
          @keyframes fadeInRight {
            from {
              opacity: 0;
              transform: translateX(20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          .animate-fadeInRight {
            animation: fadeInRight 0.3s ease-out;
          }
        `}
      </style>
      
      {/* Success notification */}
      {showSuccess && (
        <div className="fixed top-4 right-4 z-50 animate-fadeInRight">
          <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
            <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}
      
      {/* Error notification */}
      {showError && (
        <div className="fixed top-4 right-4 z-50 animate-fadeInRight">
          <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
            <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">{errorMessage}</span>
          </div>
        </div>
      )}
      
      {/* Header with back button */}
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/admin/campaigns')}
          className="text-white hover:text-gray-200 mr-4"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-white">EDIT CAMPAIGN</h1>
          {(campaign?.name || formData.name) && (
            <p className="text-lg text-white/80 mt-1">{campaign?.name || formData.name}</p>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <nav aria-label="Campaign sections" className="bg-white/10 backdrop-blur-sm rounded-2xl p-1 mb-6">
        <div className="md:hidden">
          <label htmlFor="campaign-edit-tab" className="sr-only">Campaign section</label>
          <div className="relative">
            <select
              id="campaign-edit-tab"
              value={activeTab}
              onChange={(e) => selectCampaignTab(e.target.value)}
              className="w-full appearance-none bg-transparent text-white rounded-xl px-4 py-3 pr-10 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-white/40"
            >
              {campaignTabs.map((tab) => (
                <option key={tab.id} value={tab.id} className="text-gray-900">
                  {tab.label}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-white/80" aria-hidden="true">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </div>
        </div>

        <div role="tablist" className="hidden md:flex md:flex-wrap md:gap-1">
          {campaignTabs.map((tab) => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                title={tab.label}
                onClick={() => selectCampaignTab(tab.id)}
                className={`px-2.5 lg:px-3 2xl:px-4 py-2 2xl:py-2.5 text-xs 2xl:text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-1.5 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-white text-teal-800 shadow-lg'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                <TabIcon className="w-4 h-4 shrink-0" strokeWidth={2} aria-hidden="true" />
                <span className="2xl:hidden">{tab.shortLabel}</span>
                <span className="hidden 2xl:inline">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
      
      <div className="bg-white rounded-3xl shadow-2xl">
        <div key={activeTab}>
        <>
        {activeTab === 'details' ? (
          <form onSubmit={handleSubmit} className="p-8">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl mb-6">
                {error}
              </div>
            )}

            <div className="space-y-6">
              <div>
                <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                  Campaign Name
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                  Description (Rich Text)
                </label>
                <WysiwygEditor
                  value={formData.description}
                  onChange={(html) => setFormData(prev => ({ ...prev, description: html }))}
                  placeholder="Enter campaign description..."
                />
              </div>

              <div>
                <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                  Terms and Conditions (PDF Upload)
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
                  {formData.terms_and_conditions ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center space-x-2 text-green-600">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-medium">{formData.terms_and_conditions.split('/').pop()}</span>
                      </div>
                      <div className="flex items-center justify-center space-x-4">
                        <a
                          href={`${ASSET_BASE_URL}${formData.terms_and_conditions}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View PDF
                        </a>
                        <input
                          type="file"
                          id="terms-file-replace"
                          accept=".pdf"
                          onChange={handleTermsUpload}
                          className="hidden"
                        />
                        <label
                          htmlFor="terms-file-replace"
                          className="cursor-pointer inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                          </svg>
                          Replace PDF
                        </label>
                        <button
                          type="button"
                          onClick={handleDeleteTerms}
                          className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="file"
                        id="terms-file"
                        accept=".pdf"
                        onChange={handleTermsUpload}
                        className="hidden"
                      />
                      <label
                        htmlFor="terms-file"
                        className="cursor-pointer inline-flex items-center px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        Upload PDF Terms
                      </label>
                      <p className="text-sm text-gray-500 mt-2">
                        Upload a PDF file containing the terms and conditions. Maximum file size: 10MB.
                      </p>
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  This PDF will be displayed to users before they submit their entry. Users must agree to these terms to participate.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                    Start Date
                  </label>
                  <DateInput
                    name="start_date"
                    required
                    value={formData.start_date}
                    onChange={handleChange}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                    End Date
                  </label>
                  <DateInput
                    name="end_date"
                    required
                    value={formData.end_date}
                    onChange={handleChange}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                  Promotion Mechanics Type
                </label>
                <select
                  name="mechanics_type_id"
                  value={formData.mechanics_type_id || ''}
                  onChange={(e) => {
                    const mechanicsId = e.target.value;
                    const selected = availableMechanics.find(m => String(m.id) === mechanicsId);
                    setFormData(prev => ({
                      ...prev,
                      mechanics_type_id: mechanicsId,
                      mechanics_type: selected ? selected.label : '',
                      mechanics_type_code: selected ? (selected.type || '') : ''
                    }));
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="">Select mechanics type</option>
                  {availableMechanics.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                  Entry Limits
                </label>
                <div className="space-y-4">
                  {/* Per Period Limits */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Entry Frequency Limits</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Maximum Entries <span className="text-xs font-normal text-gray-500">(per period)</span>
                        </label>
                        <div className="space-y-2">
                          <input
                            type="number"
                            name="max_entries_per_user"
                            min="1"
                            value={formData.max_entries_per_user}
                            onChange={handleChange}
                            disabled={formData.unlimited_entries}
                            placeholder="1"
                            className={`px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent w-full ${
                              formData.unlimited_entries ? 'bg-gray-100 text-gray-500' : ''
                            }`}
                          />
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              name="unlimited_entries"
                              checked={formData.unlimited_entries}
                              onChange={handleChange}
                              className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                            />
                            <span className="ml-2 text-sm text-gray-600">
                              <strong>Unlimited entries</strong> per user
                            </span>
                          </label>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Reset Period
                        </label>
                        <select
                          name="entry_limit_period"
                          value={formData.entry_limit_period}
                          onChange={handleChange}
                          disabled={formData.unlimited_entries}
                          className={`px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent w-full ${
                            formData.unlimited_entries ? 'bg-gray-100 text-gray-500' : ''
                          }`}
                        >
                          <option value="none">No Reset (Total Campaign)</option>
                          <option value="daily">Daily Reset</option>
                          <option value="weekly">Weekly Reset</option>
                        </select>
                        {!formData.unlimited_entries && formData.entry_limit_period !== 'none' && (
                          <p className="text-xs text-gray-500 mt-1">
                            Entry count resets {formData.entry_limit_period === 'daily' ? 'every day at midnight' : 'every week'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Total Campaign Limit */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Campaign Total Limit</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Maximum Total Entries <span className="text-xs font-normal text-gray-500">(entire campaign)</span>
                      </label>
                      <div className="space-y-2">
                        <input
                          type="number"
                          name="max_total_entries"
                          min="1"
                          value={formData.max_total_entries}
                          onChange={handleChange}
                          disabled={formData.unlimited_total_entries}
                          placeholder="Leave empty for no limit"
                          className={`px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent w-full ${
                            formData.unlimited_total_entries ? 'bg-gray-100 text-gray-500' : ''
                          }`}
                        />
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            name="unlimited_total_entries"
                            checked={formData.unlimited_total_entries}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setFormData(prev => ({
                                ...prev,
                                unlimited_total_entries: checked,
                                max_total_entries: checked ? '' : prev.max_total_entries
                              }));
                            }}
                            className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                          />
                          <span className="ml-2 text-sm text-gray-600">
                            <strong>No limit</strong> on total entries per user
                          </span>
                        </label>
                        <p className="text-xs text-gray-500">
                          This is the maximum total entries a user can submit throughout the entire campaign
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Explanation Box */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>📋 Current Settings:</strong><br/>
                      {formData.unlimited_entries ? (
                        <>
                          • Users can submit <strong>unlimited entries</strong>
                          {formData.unlimited_total_entries ? (
                            <> with <strong>no maximum limit</strong></>
                          ) : (
                            <> but are limited to <strong>{formData.max_total_entries} total entries</strong> for the entire campaign</>
                          )}
                        </>
                      ) : formData.entry_limit_period === 'none' ? (
                        <>
                          • Users can submit <strong>{formData.max_entries_per_user || 1} {(formData.max_entries_per_user || 1) === 1 ? 'entry' : 'entries'} total</strong>
                          {formData.unlimited_total_entries ? (
                            <> (this is also their maximum for the campaign)</>
                          ) : (
                            <> with a campaign maximum of <strong>{formData.max_total_entries} entries</strong></>
                          )}
                        </>
                      ) : (
                        <>
                          • Users can submit <strong>{formData.max_entries_per_user || 1} {(formData.max_entries_per_user || 1) === 1 ? 'entry' : 'entries'}</strong> {formData.entry_limit_period === 'daily' ? 'per day' : 'per week'}
                          {formData.unlimited_total_entries ? (
                            <> with <strong>no maximum limit</strong> for the campaign</>
                          ) : (
                            <> but cannot exceed <strong>{formData.max_total_entries} total entries</strong> for the campaign</>
                          )}
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="requires_receipt"
                    checked={formData.requires_receipt}
                    onChange={handleChange}
                    className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Requires Receipt Upload</span>
                </label>
                
                {formData.requires_receipt ? (
                  <label className="flex items-center ml-6">
                    <input
                      type="checkbox"
                      name="skip_receipt_validation"
                      checked={formData.skip_receipt_validation}
                      onChange={handleChange}
                      className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Skip receipt validation</span>
                  </label>
                ) : null}
                
              </div>
            </div>
            
            {/* Save Button */}
            <div className="flex justify-end pt-6">
              <button
                type="submit"
                disabled={saving}
                className="px-8 py-3 bg-teal-700 text-white rounded-xl hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        // ) : activeTab === 'faqs' ? (
        //   <div className="p-8">
        //     <h3 className="text-xl font-bold text-gray-900 mb-6">Manage FAQs</h3>
        //     
        //     <div className="mb-6 bg-gray-50 p-6 rounded-xl">
        //       <h4 className="text-sm font-medium text-gray-700 mb-4">Add New FAQ</h4>
        //       <div className="space-y-4">
        //         <input
        //           type="text"
        //           value={newFaq.question}
        //           onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
        //           className="w-full px-4 py-3 border border-gray-300 rounded-xl"
        //           placeholder="Enter question..."
        //         />
        //         <textarea
        //           value={newFaq.answer}
        //           onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
        //           className="w-full px-4 py-3 border border-gray-300 rounded-xl"
        //           rows={3}
        //           placeholder="Enter answer..."
        //         />
        //         <button
        //           type="button"
        //           onClick={handleAddFaq}
        //           className="px-6 py-3 bg-teal-700 text-white rounded-xl hover:bg-teal-600"
        //         >
        //           Add FAQ
        //         </button>
        //       </div>
        //     </div>

        //     <div className="space-y-4">
        //       {faqs.map((faq, index) => (
        //         <div key={faq.id} className="border border-gray-200 rounded-xl p-6">
        //           <div className="flex justify-between items-start">
        //             <div className="flex-1 space-y-4">
        //               <div>
        //                 <label className="block text-sm font-medium text-gray-700 mb-2">
        //                   Question {index + 1}
        //                 </label>
        //                 <input
        //                   type="text"
        //                   value={faq.question}
        //                   onChange={(e) => handleUpdateFaq(faq.id, { question: e.target.value })}
        //                   className="w-full px-4 py-2 border border-gray-300 rounded-lg"
        //                 />
        //               </div>
        //               <div>
        //                 <label className="block text-sm font-medium text-gray-700 mb-2">
        //                   Answer
        //                 </label>
        //                 <textarea
        //                   value={faq.answer}
        //                   onChange={(e) => handleUpdateFaq(faq.id, { answer: e.target.value })}
        //                   className="w-full px-4 py-2 border border-gray-300 rounded-lg"
        //                   rows={3}
        //                 />
        //               </div>
        //             </div>
        //             <button
        //               type="button"
        //               onClick={() => handleDeleteFaq(faq.id)}
        //               className="ml-4 text-red-600 hover:text-red-800"
        //             >
        //               <Trash2 className="h-5 w-5" />
        //             </button>
        //           </div>
        //         </div>
        //       ))}
        //     </div>
        //   </div>
        // ) : activeTab === 'gallery' ? (
        //   <div className="p-8">
        //     <h3 className="text-xl font-bold text-gray-900 mb-6">Manage Gallery Images</h3>
        //     
        //     <div className="mb-6">
        //       <div className="border-2 border-gray-300 border-dashed rounded-xl p-6 text-center hover:border-teal-600 transition-colors">
        //         <label className="cursor-pointer">
        //           <CloudUpload className="mx-auto h-12 w-12 text-gray-400 mb-3" />
        //           <span className="text-sm text-gray-600">
        //             {uploadingImage ? 'Uploading...' : 'Click to upload image'}
        //           </span>
        //           <input
        //             type="file"
        //             className="sr-only"
        //             accept="image/jpeg,image/jpg,image/png"
        //             onChange={handleImageUpload}
        //             disabled={uploadingImage}
        //           />
        //         </label>
        //         <p className="text-xs text-gray-500 mt-2">JPG, PNG up to 5MB</p>
        //       </div>
        //     </div>

        //     <div className="grid grid-cols-3 gap-6">
        //       {gallery.map((image, index) => (
        //         <div key={image.id} className="relative group">
        //           <img
        //             src={`${ASSET_BASE_URL}${image.image_url}`}
        //             alt={image.title || `Gallery image ${index + 1}`}
        //             className="w-full h-48 object-cover rounded-xl"
        //           />
        //           <button
        //             onClick={() => handleDeleteGalleryImage(image.id)}
        //             className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
        //           >
        //             <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        //               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        //             </svg>
        //           </button>
        //         </div>
        //       ))}
        //     </div>
        //   </div>
        ) : activeTab === 'products' ? (
          <div className="p-8">
            <ProductSelection
              campaign={campaign}
              selectedProducts={selectedProducts}
              allProducts={allProducts}
              onUpdate={(updatedSelectedProducts, shouldRefreshProducts) => {
                setSelectedProducts(updatedSelectedProducts);
                if (shouldRefreshProducts) {
                  fetchAllProducts();
                }
              }}
              api={api}
              setSuccessMessage={setSuccessMessage}
              setShowSuccess={setShowSuccess}
              setErrorMessage={setErrorMessage}
              setShowError={setShowError}
            />
          </div>
        ) : activeTab === 'theme' ? (
          <div className="p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Theme & Layout Configuration</h3>
            
          <ThemesAndLayout
            campaign={campaign}
            theme={theme}
            sections={sections}
            winnerSections={winnerSections}
            comingSoonSections={comingSoonSections}
            uploadingLogo={uploadingLogo}
            uploadingBackground={uploadingBackground}
            onThemeUpdate={handleThemeUpdate}
            onSectionsChange={setSections}
            onWinnerSectionsChange={setWinnerSections}
            onComingSoonSectionsChange={setComingSoonSections}
            onSave={handleSaveTheme}
            onLogoUpload={handleLogoUpload}
            onRemoveLogo={handleRemoveLogo}
            onBackgroundUpload={handleBackgroundUpload}
            onRemoveBackground={handleRemoveBackground}
            campaignId={id}
            api={api}
            onAddSection={handleAddLandingSection}
            onAddWinnerSection={handleAddWinnerSection}
            onAddComingSoonSection={handleAddComingSoonSection}
            onDeleteSection={handleDeleteLandingSection}
            onDeleteWinnerSection={handleDeleteWinnerSection}
            onDeleteComingSoonSection={handleDeleteComingSoonSection}
            onDuplicateSection={handleDuplicateLandingSection}
            onDuplicateWinnerSection={handleDuplicateWinnerSection}
            onDuplicateComingSoonSection={handleDuplicateComingSoonSection}
            onApplyTemplate={handleApplyTemplate}
            setSuccessMessage={setSuccessMessage}
            setShowSuccess={setShowSuccess}
            setErrorMessage={setErrorMessage}
            setShowError={setShowError}
            />
          </div>
        ) : activeTab === 'forms' ? (
          <FormSelection
            campaignId={id}
            campaignName={campaign?.name}
            campaignSlug={campaign?.slug}
            activeForm={activeForm}
            allForms={allForms}
            formFields={formFields}
            api={api}
            setAllForms={setAllForms}
            setActiveForm={setActiveForm}
            setFormFields={setFormFields}
            setForms={setForms}
            setSuccessMessage={setSuccessMessage}
            setShowSuccess={setShowSuccess}
            setErrorMessage={setErrorMessage}
            setShowError={setShowError}
          />
        ) : activeTab === 'instantwin' ? (
          <div className="p-8">
            <InstantWinConfig
              campaignId={id}
              enabled={instantWinEnabled}
              onEnabledChange={setInstantWinEnabled}
              distribution={instantWinDistribution}
              onDistributionChange={setInstantWinDistribution}
              prizes={instantWinPrizes}
              onPrizesChange={setInstantWinPrizes}
              formFields={formFields}
              api={api}
              stats={instantWinStats}
              config={instantWinConfig}
              onConfigChange={setInstantWinConfig}
              setSuccessMessage={setSuccessMessage}
              setShowSuccess={setShowSuccess}
              setErrorMessage={setErrorMessage}
              setShowError={setShowError}
            />
          </div>
        ) : activeTab === 'cashback' ? (
          <div className="p-8">
            <CashbackConfig
              campaignId={id}
              enabled={cashbackVoucherEnabled}
              mechanicsType={formData.mechanics_type}
              mechanicsTypeCode={formData.mechanics_type_code}
              onEnabledChange={setCashbackVoucherEnabled}
              api={api}
              setSuccessMessage={setSuccessMessage}
              setShowSuccess={setShowSuccess}
              setErrorMessage={setErrorMessage}
              setShowError={setShowError}
            />
          </div>
        ) : activeTab === 'membership' ? (
          <div className="p-8">
            <MembershipConfig
              campaignId={id}
              campaignName={campaign?.name || ''}
              api={api}
              setSuccessMessage={setSuccessMessage}
              setShowSuccess={setShowSuccess}
              setErrorMessage={setErrorMessage}
              setShowError={setShowError}
            />
          </div>
        ) : activeTab === 'emails' ? (
          <div className="p-8">
            <EmailSettings
              campaignId={id}
              settings={emailSettings}
              onUpdate={(updatedSettings) => {
                setEmailSettings(updatedSettings);
              }}
              api={api}
              campaignName={campaign?.name || ''}
              setSuccessMessage={setSuccessMessage}
              setShowSuccess={setShowSuccess}
              theme={theme}
              activeForm={activeForm}
              formFields={formFields}
            />
          </div>
        ) : activeTab === 'qrcode' ? (
          <div className="p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Campaign QR Codes</h3>
            
            <div className="space-y-8">
              {/* Campaign QR Code */}
              {campaign && (
                <QRCodeGenerator
                  url={`${process.env.REACT_APP_HOSTNAME || window.location.origin}/campaign/${campaign.slug}`}
                  title="Campaign QR Code"
                  filename={`campaign-${campaign.slug}`}
                  containerId="qrcode-container"
                />
              )}

              {/* Custom Domain QR Code */}
              {campaign && campaign.custom_domain && campaign.custom_domain.trim() !== '' && (
                <div className="border-t pt-8">
                  <QRCodeGenerator
                    url={`https://${campaign.custom_domain.trim()}`}
                    title="Custom Domain QR Code"
                    filename={`custom-domain-${campaign.custom_domain.trim().replace(/[^a-zA-Z0-9]/g, '-')}`}
                    containerId="custom-domain-qrcode-container"
                  />
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'winnerdraw' ? (
          <WinnerDraw 
            campaignId={id} 
            campaign={campaign} 
            setSuccessMessage={setSuccessMessage}
            setShowSuccess={setShowSuccess}
          />
        ) : activeTab === 'settings' ? (
          <div className="p-8">
            <CampaignSettings
              campaignId={id}
              formData={formData}
              savedCustomDomain={campaign?.custom_domain || ''}
              onChange={handleChange}
              onSubmit={handleSubmit}
              saving={saving}
              error={error}
            />
          </div>
        ) : null}
      {/* Section Type Selection Modal */}
      {showSectionTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 shadow-2xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Add New Section</h3>
            <p className="text-gray-600 mb-8">Choose a section type to add to your campaign</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {/* Banner Section */}
              <div 
                className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                  selectedSectionType === 'banner' 
                    ? 'border-teal-500 bg-teal-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onClick={() => setSelectedSectionType('banner')}
              >
                <div className="flex items-start space-x-3">
                  <input
                    type="radio"
                    name="sectionType"
                    value="banner"
                    checked={selectedSectionType === 'banner'}
                    onChange={() => {}}
                    className="mt-1 h-4 w-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 flex items-center">
                      <span className="mr-2">🖼️</span> Hero Banner
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Eye-catching banner with background image or video
                    </p>
                  </div>
                </div>
              </div>

              {/* Content Section */}
              <div 
                className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                  selectedSectionType === 'content' 
                    ? 'border-teal-500 bg-teal-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onClick={() => setSelectedSectionType('content')}
              >
                <div className="flex items-start space-x-3">
                  <input
                    type="radio"
                    name="sectionType"
                    value="content"
                    checked={selectedSectionType === 'content'}
                    onChange={() => {}}
                    className="mt-1 h-4 w-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 flex items-center">
                      <span className="mr-2">📝</span> Content
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Text content with optional form or background
                    </p>
                  </div>
                </div>
              </div>

              {/* Forms Section - Only for landing pages */}
              {currentPageType === 'landing_page' && (
                <div 
                  className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                    selectedSectionType === 'forms' 
                      ? 'border-teal-500 bg-teal-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onClick={() => setSelectedSectionType('forms')}
                >
                  <div className="flex items-start space-x-3">
                    <input
                      type="radio"
                      name="sectionType"
                      value="forms"
                      checked={selectedSectionType === 'forms'}
                      onChange={() => {}}
                      className="mt-1 h-4 w-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                    />
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 flex items-center">
                        <span className="mr-2">📋</span> Entry Form
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Standalone form section for entries
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* FAQs Section */}
              <div 
                className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                  selectedSectionType === 'faqs' 
                    ? 'border-teal-500 bg-teal-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onClick={() => setSelectedSectionType('faqs')}
              >
                <div className="flex items-start space-x-3">
                  <input
                    type="radio"
                    name="sectionType"
                    value="faqs"
                    checked={selectedSectionType === 'faqs'}
                    onChange={() => {}}
                    className="mt-1 h-4 w-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 flex items-center">
                      <span className="mr-2">❓</span> FAQ Section
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Frequently asked questions with answers
                    </p>
                  </div>
                </div>
              </div>

              {/* Gallery Section */}
              <div 
                className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                  selectedSectionType === 'gallery' 
                    ? 'border-teal-500 bg-teal-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onClick={() => setSelectedSectionType('gallery')}
              >
                <div className="flex items-start space-x-3">
                  <input
                    type="radio"
                    name="sectionType"
                    value="gallery"
                    checked={selectedSectionType === 'gallery'}
                    onChange={() => {}}
                    className="mt-1 h-4 w-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 flex items-center">
                      <span className="mr-2">🖼️</span> Image Gallery
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Grid of images with optional titles
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowSectionTypeModal(false);
                  setSelectedSectionType('');
                }}
                className="px-6 py-3 text-gray-700 bg-gray-200 rounded-xl hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmAddSection}
                className="px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors"
              >
                Add Section
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      </div>
      </div>
    </AdminLayout>
  );
}

export default CampaignEdit;
